import {
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import BookIcon from '@src/svgs/BookIcon';
import VideoIcon from '@src/svgs/VideoIcon';
import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

/** Wide enough for the two labels; the trigger is 16px, so it cannot set this. */
const MENU_WIDTH = 180;

/**
 * Room the rule leaves at the right for the timestamp: `mm:ss` at `--text-xs`,
 * plus the 10px the rows are inset by, plus a gap so the line does not run into
 * the digits. A reel past an hour would read `mm:ss` past 60 minutes, which is
 * the same thing every other duration in this panel does.
 */
const TIMESTAMP_GUTTER = 48;

interface InsertDividerProps {
  /** Names the gap for test ids: a Section's 1-based position, or `end`. */
  gap: string;
  /**
   * Where this gap is, in words — "before section 2", "at the end of the reel".
   * Read into the trigger's label, so the position is stated once here rather
   * than phrased separately for every action.
   */
  position: string;
  /**
   * Shared tooltip instance on the panel; without it the button falls back to
   * no tooltip at all rather than borrowing another surface's style.
   */
  tooltipId?: string;
  /**
   * False leaves the rule with no gesture on it: a drag is in flight, when the
   * gap already means "drop here" (PLAT-11053) and one gap carries one meaning
   * at a time. The rule stays put, so the list does not shift under the pointer.
   */
  canInsert?: boolean;
  /**
   * Whether a Chapter is representable in this gap (PLAT-11055 DS6/DS7). False
   * takes Add Chapter off the menu and leaves Add Clip(s) — there is always a
   * clip that can go in a gap (PLAT-11068 DS3).
   */
  canInsertChapter?: boolean;
  onInsertChapter?: () => void;
  onAddClips?: () => void;
  /**
   * The gap is also a drop target while a drag is in flight (PLAT-11075 DS5).
   * Without these the hairline is a dead strip: HTML5 DnD only allows a drop
   * where `dragover` called `preventDefault`, so releasing on an unhandled
   * 13px gap cancels the drag with no feedback. The insertion line itself is
   * still drawn by the row this gap sits against.
   */
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  /**
   * PLAT-11056 — a Chapter dropped here goes to the end of the reel, and this
   * rule draws that line itself: past the last Chapter there is no heading
   * below to carry one, the way every other Chapter target has.
   */
  isDropTarget?: boolean;
  /**
   * Where this gap falls in the finished reel, as `mm:ss` — the transcript's
   * own right-aligned timestamp, on the boundary between two Sections rather
   * than a paragraph break. Derived from playback order, so reordering the reel
   * renumbers every gap below the change with no bookkeeping of its own.
   */
  timestamp?: string;
}

/**
 * PLAT-11055 — the hairline between two Sections, and the gesture that lives
 * in it: a chapter *is* a boundary (DE12), and the gap between two rows is that
 * boundary made visible, so the thing clicked and the thing stored are the same
 * thing (DS1).
 *
 * The same rule opens and closes the list, so the first and last Sections are
 * bounded the way the ones between them are — and the closing one inserts too:
 * a chapter past the last Section is an empty holder to add into (DS7).
 *
 * Hover- and focus-revealed rather than a permanent row between every pair of
 * Sections, matching the row's hover-revealed rename pencil.
 *
 * PLAT-11068 — the gap holds *two* things now, so the `+` asks which: a Chapter,
 * or clips from the story (DS1). The gap is the position either way; the menu is
 * only what goes in it.
 */
export const InsertDivider: React.FC<InsertDividerProps> = ({
  gap,
  position,
  tooltipId,
  canInsert = true,
  canInsertChapter = true,
  onInsertChapter,
  onAddClips,
  onDragOver,
  onDrop,
  isDropTarget = false,
  timestamp,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  // Portaled and self-dismissing (DS5): the gaps live inside the Section list,
  // which scrolls, so an in-panel menu would be clipped by it — and the panel
  // itself is inside the clip-preview modal, so the menu needs the
  // above-the-backdrop z-tier. The hook owns both, plus outside click, Escape,
  // scroll and resize, which a wrapper-scoped click-outside cannot once the menu
  // has left this component's subtree.
  //
  // Opens upward when the gap is too near the bottom of the window for the menu
  // to fit under it: every gap in the list is a trigger, so on a short window
  // the last ones sit against the viewport edge — and clamped-and-scrolling (the
  // below-only fallback) turns a two-item menu into a sliver. `auto` still
  // prefers below, so the flip only happens where it is needed — measured
  // against the menu's rendered height, so item count sets it, not a constant.
  const { menuRef, style } = useAnchoredDropdown(
    isMenuOpen,
    triggerRef,
    closeMenu,
    undefined,
    { width: MENU_WIDTH, placement: 'auto' },
  );

  const insertChapter = canInsertChapter ? onInsertChapter : undefined;
  const hasMenu = canInsert && Boolean(insertChapter || onAddClips);

  const pick = (action: () => void) => {
    closeMenu();
    action();
  };

  return (
    <Gap
      data-testid={`reel-section-divider-${gap}`}
      // The rule going green *is* the insertion line (PLAT-11075 DS12), so this
      // is what says out loud that exactly one gap in the list is lit.
      data-drop-target={isDropTarget || undefined}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <Rule $isDropTarget={isDropTarget} $hasTimestamp={!!timestamp} />
      {timestamp && (
        <Timestamp data-testid={`reel-divider-timestamp-${gap}`}>
          {timestamp}
        </Timestamp>
      )}
      {hasMenu && (
        <InsertButton
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          aria-label={
            insertChapter
              ? `Add a chapter or clip ${position}`
              : `Add a clip ${position}`
          }
          data-testid={`reel-insert-${gap}`}
          // Always an anchor, even with nothing to say. Dropping the *id* while
          // the tooltip was open sent react-tooltip looking for another anchor
          // with this shared id, and it took the first one in the document — the
          // utility bar's search button — so opening this menu flashed "Search
          // the reel" up beside the search icon. Emptying the *content* leaves
          // the anchor alone and renders nothing.
          data-tooltip-id={tooltipId}
          // Nothing while the menu is open: the menu names both actions, in more
          // words than the tooltip has (DS6).
          data-tooltip-content={
            isMenuOpen ? '' : insertChapter ? 'Add Chapter or Clip' : 'Add Clip'
          }
          $isMenuOpen={isMenuOpen}
          onClick={() => setIsMenuOpen(open => !open)}
        >
          Insert
        </InsertButton>
      )}
      {isMenuOpen &&
        style &&
        createPortal(
          <ContextMenuSurface
            ref={menuRef}
            style={style}
            role="menu"
            data-testid={`reel-insert-menu-${gap}`}
          >
            {insertChapter && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                data-testid={`reel-insert-menu-chapter-${gap}`}
                onClick={() => pick(insertChapter)}
              >
                <BookIcon strokeColor="currentColor" />
                Add Chapter
              </ContextMenuItem>
            )}
            {onAddClips && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                data-testid={`reel-insert-menu-clips-${gap}`}
                onClick={() => pick(onAddClips)}
              >
                <VideoIcon strokeColor="currentColor" />
                Add Clip(s)
              </ContextMenuItem>
            )}
          </ContextMenuSurface>,
          document.body,
        )}
    </Gap>
  );
};

const Gap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 13px of affordance plus 2px of air above and below, so the boundary
     doesn't sit tight against the rows it separates. */
  height: 17px;
  flex-shrink: 0;
  /* The gap lights up the way a Section row does — same fill, same radius, same
     motion — so "this strip is a target" is the one hover language in the panel
     rather than a second one invented for the divider. */
  border-radius: var(--radius-input);
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

/** Inset to the rows' padding, so the list reads as one column. */
const Rule = styled.span<{ $isDropTarget?: boolean; $hasTimestamp?: boolean }>`
  position: absolute;
  left: 10px;
  /* Stops short of the timestamp rather than running under it, so the two read
     as one line ending in a time — the transcript's own arrangement. */
  right: ${props => (props.$hasTimestamp ? `${TIMESTAMP_GUTTER}px` : '10px')};
  /* As a drop target it thickens into the insertion line the rows draw, so one
     gesture has one rendering wherever it lands (PLAT-11056). */
  height: ${props => (props.$isDropTarget ? '2px' : '1px')};
  border-radius: ${props => (props.$isDropTarget ? '1px' : '0')};
  /* Invisible at rest: with a dozen Sections in the list, a hairline between
     every pair is more ruling than the reel needs to read as a list — the
     spacing already separates them. It arrives with the pointer, where it says
     "there is a boundary here, and a gesture in it". */
  background-color: ${props =>
    props.$isDropTarget ? 'var(--primary)' : 'transparent'};
  transition: background-color var(--transition-fast);

  /* Lifts to a dull grey with the pointer. The gap's own fill is what says
     "target" (see Gap); the rule only has to stay legible on top of it, so this
     stops at the standard hover escalation rather than going bright. */
  ${Gap}:hover & {
    background-color: ${props =>
      props.$isDropTarget ? 'var(--primary)' : 'var(--text-muted)'};
  }
`;

/**
 * Where this gap falls in the reel. Always on, unlike the rule beside it: it is
 * information about the reel rather than an affordance, and it is what tells the
 * creator a clip moved without their having to hover every boundary to find out.
 *
 * Right-aligned and muted at `--text-xs`, the transcript's `TimestampRow` in the
 * one place this panel has for it.
 */
const Timestamp = styled.span`
  position: absolute;
  right: 10px;
  color: var(--text-muted);
  /* A step below --text-xs: at 12px this boundary chrome sat as large as the
     chapter meta and the rows' own text; at 10px it vanished. 11px keeps it
     readable while still under the content text. */
  font-size: 11px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  user-select: none;
  pointer-events: none;
`;

/**
 * Says what it does in a word rather than as a `+`: the gap holds two different
 * inserts now (a Chapter or clips), which is more than a plus glyph can carry —
 * and the label is legible the moment the line is hovered, before the tooltip
 * has had time to arrive.
 *
 * Absolutely centred over the rule so the wider chip cannot grow the 17px gap
 * and shift the list under the pointer.
 */
const InsertButton = styled.button<{ $isMenuOpen: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  padding: 0 8px;
  border: 1px solid var(--primary);
  border-radius: 999px;
  /* Filled rather than transparent: the word sits on top of the rule, and an
     unfilled chip would have the line running through the letters. */
  background-color: var(--surface-elevated);
  color: var(--primary);
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  opacity: 0;
  transition:
    opacity var(--transition-fast),
    background-color var(--transition-fast);

  /* Anywhere on the line reveals it — the gap is the target, not the chip, so
     the label is already there when the pointer arrives at the middle. */
  ${Gap}:hover &,
  &:focus-visible {
    opacity: 1;
  }

  /* An open menu holds the reveal (DS6): moving the pointer into the menu leaves
     the gap, and without this the trigger would fade out from under its own
     menu. */
  ${props =>
    props.$isMenuOpen &&
    css`
      opacity: 1;
    `}

  &:hover {
    background-color: var(--primary);
    color: var(--background);
  }
`;
